import Modal from 'components/Modal';
import Typography from 'components/Typography';
import Select from 'components/Select';
import {components, SingleValue} from 'react-select';
import {groupBy, isArray, map, sortBy} from 'lodash';
import {reject} from 'lodash';
import DateTimePicker from 'components/DateTimePicker';
import calendar from 'pages/DeliveryPlanner/icons/calendar.svg';
import Button from 'components/Button';
import classNames from 'classnames';
import styles from './OrdersFilterModal.module.scss';
import locationIcon from '../location.svg';
import {attemptSelectOptions} from 'constants/select-options';
import {Order, SelectOption} from 'types';
import PriorityFlag from 'components/PriorityFlag';
import {FilterState} from '../useFilter';
import nyPostalCodeBoroughOptions from 'shared/ny-postal-code-borough-options';
import Morning from 'pages/DeliveryPlanner/icons/morning';
import Afternoon from 'pages/DeliveryPlanner/icons/afternoon';
import Evening from 'pages/DeliveryPlanner/icons/evening';
import stat_time from 'pages/DeliveryPlanner/icons/stat_time.svg';
import useAuth from 'hooks/useAuth';
import FeatureGuard from 'components/FeatureGuard';
import useCompanyConfig from 'hooks/useCompanyConfig';

const locationOptionsGrouped = groupBy(nyPostalCodeBoroughOptions, 'label');
const sorted: {label: string; value: any}[] = map(
  locationOptionsGrouped,
  (obj, key) => ({
    label: key,
    value: map(obj, 'value'),
  })
);
const locationOptions = sortBy(sorted, 'label');

interface OrdersFilterModalProps {
  isOpen: boolean;
  onFilterModalClose: () => void;
  tempFilters: FilterState;
  setTempFilters: React.Dispatch<React.SetStateAction<FilterState>>;
  applyFilter: () => void;
  resetFilter: () => void;
  landlordSelectOptions: SelectOption[];
  propertyAddressOptions: SelectOption[];
  propertyNameOptions: SelectOption[];
}

function OrdersFilterModal({
  isOpen,
  onFilterModalClose,
  tempFilters,
  setTempFilters,
  applyFilter,
  resetFilter,
  landlordSelectOptions,
  propertyAddressOptions,
  propertyNameOptions,
}: OrdersFilterModalProps) {
  const {currentUser} = useAuth();
  const isLandlord = currentUser?.role === 'landlord';
  const {labels} = useCompanyConfig();
  const handleSelectAttempt = (
    option: SingleValue<SelectOption<string | number, React.ReactNode>>
  ) => {
    const filterOption = option as SelectOption<string, string> | undefined;
    if (option?.value === 5) {
      setTempFilters((prev: FilterState) => ({
        ...prev,
        attemptStart: filterOption,
        attemptEnd: undefined,
      }));
    } else {
      setTempFilters((prev: FilterState) => ({
        ...prev,
        attemptStart: filterOption,
        attemptEnd: filterOption,
      }));
    }
  };
  const handleApplyFilter = () => {
    applyFilter();
    onFilterModalClose();
  };
  const handleResetFilter = () => {
    resetFilter();
    onFilterModalClose();
  };
  return (
    <Modal
      isOpen={isOpen}
      className={styles.filterModal}
      onClose={onFilterModalClose}
      onRequestClose={onFilterModalClose}
    >
      <Typography variant="h2">Filter</Typography>
      <div className={styles.filterContainer}>
        <div className={styles.row}>
          <div style={{width: '100%'}}>
            <Select
              className={'DeliveryPlanner__Filter'}
              placeholder="Location"
              menuPosition="fixed"
              components={{
                Input: () => null,
                DropdownIndicator: (props) =>
                  !tempFilters.location ? (
                    <components.DropdownIndicator {...props} />
                  ) : null,
                Control: ({children, ...props}) => {
                  return (
                    <components.Control {...props}>
                      <img
                        className={styles.locationIcon}
                        src={locationIcon}
                        alt="location_icon"
                      />
                      {children}
                    </components.Control>
                  );
                },
                SingleValue: ({children, ...props}) => {
                  return (
                    <components.SingleValue {...props}>
                      {isArray(props.selectProps.value)
                        ? props.selectProps.value
                            .map((item: {label: string}) => item.label)
                            .join(', ')
                        : ''}
                    </components.SingleValue>
                  );
                },
                IndicatorSeparator: () => null,
              }}
              isSearchable={false}
              onChange={(data) => {
                let location: any = [];
                if (data) {
                  location = tempFilters.location.some(
                    (item: any) => item.label === data.label
                  )
                    ? reject(tempFilters.location, data)
                    : [...tempFilters.location, data];
                }
                setTempFilters((prev: FilterState) => ({
                  ...prev,
                  location,
                }));
              }}
              isClearable
              options={[
                {
                  label: 'Locations',
                  options: locationOptions!,
                },
              ]}
              value={tempFilters.location}
            />
          </div>

          <DateTimePicker
            icon={calendar}
            selectsRange={true}
            showTimeSelect={false}
            dateFormat="MM/dd/yy E"
            startDate={tempFilters.createdDate?.start}
            endDate={tempFilters.createdDate?.end}
            placeholder="Created Date"
            className={styles.customInput}
            onChange={(data) => {
              setTempFilters((prev: FilterState) => ({
                ...prev,
                createdDate: {
                  start: data[0] || undefined,
                  end: data[1] || undefined,
                },
              }));
            }}
          />
        </div>
        <div className={styles.row}>
          <FeatureGuard feature="hasOfferingTypeFilterOnOrders">
            <Select
              className={styles.selectCon}
              isSearchable={false}
              onChange={(val: any) => {
                setTempFilters((prev: FilterState) => ({
                  ...prev,
                  offeringType: val,
                }));
              }}
              options={[
                {label: 'Service', value: 'service'},
                {label: 'Product', value: 'product'},
              ]}
              value={tempFilters.offeringType}
              placeholder={
                <span className="select-placeholder-text">Service/Product</span>
              }
              isClearable
              components={{
                Input: () => null,
                DropdownIndicator: (props) =>
                  !tempFilters.offeringType ? (
                    <components.DropdownIndicator {...props} />
                  ) : null,
                IndicatorSeparator: () => null,
              }}
            />
          </FeatureGuard>
          <Select
            className={styles.selectCon}
            isSearchable={false}
            menuPosition="fixed"
            onChange={handleSelectAttempt}
            options={attemptSelectOptions}
            value={tempFilters.attemptStart}
            placeholder={
              <span className="select-placeholder-text">
                Redispatch Attempt
              </span>
            }
            isClearable
            components={{
              Input: () => null,
              DropdownIndicator: (props) =>
                !tempFilters.attemptStart ? (
                  <components.DropdownIndicator {...props} />
                ) : null,
              IndicatorSeparator: () => null,
            }}
          />
        </div>

        <div className={styles.row}>
          <Select
            className={classNames(styles.selectCon)}
            isSearchable={false}
            menuPosition="fixed"
            onChange={(val: any) => {
              setTempFilters((prev: FilterState) => ({
                ...prev,
                priority: val,
              }));
            }}
            options={[
              {label: 'High', value: 'high'},
              {label: 'Medium', value: 'medium'},
              {label: 'Low', value: 'low'},
            ]}
            value={tempFilters.priority}
            placeholder={
              <span className="select-placeholder-text">Priority</span>
            }
            isClearable
            components={{
              Input: () => null,
              DropdownIndicator: (props) =>
                !tempFilters.priority ? (
                  <components.DropdownIndicator {...props} />
                ) : null,
              IndicatorSeparator: () => null,
              SingleValue: (props) => {
                return (
                  <components.SingleValue
                    {...props}
                    className={styles.select_option}
                  >
                    {props.data.label}
                    <PriorityFlag
                      priority={props.data.value as Order['priority']}
                    />
                  </components.SingleValue>
                );
              },
              Option: (props) => {
                return (
                  <components.Option
                    {...props}
                    className={styles.select_option}
                  >
                    {props.data.label}
                    <PriorityFlag
                      priority={props.data.value as Order['priority']}
                    />
                  </components.Option>
                );
              },
            }}
          />

          <Select
            className={styles.selectCon}
            isSearchable={false}
            menuPosition="fixed"
            onChange={(val: any) => {
              setTempFilters((prev: FilterState) => ({
                ...prev,
                paid: val,
              }));
            }}
            options={[
              {label: 'Paid', value: 'true'},
              {label: 'Unpaid', value: 'false'},
            ]}
            value={tempFilters.paid}
            placeholder={
              <span className="select-placeholder-text">Paid/Unpaid</span>
            }
            isClearable
            components={{
              Input: () => null,
              DropdownIndicator: (props) =>
                !tempFilters.paid ? (
                  <components.DropdownIndicator {...props} />
                ) : null,
              IndicatorSeparator: () => null,
            }}
          />
        </div>

        <div className={styles.row}>
          <DateTimePicker
            icon={calendar}
            showTimeSelect={false}
            dateFormat="MM/dd/yy E"
            value={tempFilters.date}
            placeholder="Preference Date"
            className={styles.customInput}
            onChange={(data) => {
              setTempFilters((prev: FilterState) => ({
                ...prev,
                date: data,
              }));
            }}
          />

          <Select
            className={styles.selectCon}
            isSearchable={false}
            menuPosition="fixed"
            onChange={(val: any) => {
              setTempFilters((prev: FilterState) => ({
                ...prev,
                preferenceTime: val,
              }));
            }}
            options={[
              {label: 'Morning', value: 'morning'},
              {label: 'Afternoon', value: 'afternoon'},
              {label: 'Evening', value: 'evening'},
            ]}
            value={tempFilters.preferenceTime}
            placeholder={
              <span className="select-placeholder-text">Preference Time</span>
            }
            isClearable
            components={{
              Input: () => null,
              DropdownIndicator: (props) =>
                !tempFilters.preferenceTime ? (
                  <components.DropdownIndicator {...props} />
                ) : null,
              Control: ({children, ...props}) => (
                <components.Control {...props}>
                  <img
                    className={styles.timeIcon}
                    src={stat_time}
                    alt="timer"
                  />
                  {children}
                </components.Control>
              ),

              IndicatorSeparator: () => null,
              Option: (props) => {
                const icons = {
                  morning: <Morning />,
                  afternoon: <Afternoon />,
                  evening: <Evening />,
                };
                return (
                  <components.Option
                    {...props}
                    className={styles.select_option}
                  >
                    {props.data.label}
                    {icons[props.data.value as keyof typeof icons]}
                  </components.Option>
                );
              },
            }}
          />
        </div>
        <FeatureGuard feature="hasLandlordFilterOnOrders">
          {!isLandlord && (
            <div className={styles.row}>
              <Select
                className={styles.selectCon}
                isSearchable={false}
                menuPosition="fixed"
                onChange={(val: any) => {
                  setTempFilters((prev: FilterState) => ({
                    ...prev,
                    landlord: val,
                  }));
                }}
                options={landlordSelectOptions as any}
                value={tempFilters.landlord}
                placeholder={
                  <span className="select-placeholder-text">
                    {labels.landlord}
                  </span>
                }
                isClearable
                components={{
                  Input: () => null,
                  DropdownIndicator: (props) =>
                    !tempFilters.landlord ? (
                      <components.DropdownIndicator {...props} />
                    ) : null,
                  IndicatorSeparator: () => null,
                }}
              />
              <Select
                className={styles.selectCon}
                isSearchable={false}
                menuPosition="fixed"
                onChange={(val: any) => {
                  setTempFilters((prev: FilterState) => ({
                    ...prev,
                    propertyName: val,
                  }));
                }}
                options={propertyNameOptions as any}
                value={tempFilters.propertyName}
                placeholder={
                  <span className="select-placeholder-text">
                    {labels.property} Name
                  </span>
                }
                isClearable
                components={{
                  Input: () => null,
                  DropdownIndicator: (props) =>
                    !tempFilters.propertyName ? (
                      <components.DropdownIndicator {...props} />
                    ) : null,
                  IndicatorSeparator: () => null,
                }}
              />
            </div>
          )}
        </FeatureGuard>
        <FeatureGuard feature="hasPropertyAddressFilterOnOrders">
          {!isLandlord && (
            <div className={styles.row}>
              <Select
                className={styles.selectCon}
                isSearchable={false}
                menuPosition="fixed"
                onChange={(val: any) => {
                  setTempFilters((prev: FilterState) => ({
                    ...prev,
                    propertyAddress: val,
                  }));
                }}
                options={propertyAddressOptions as any}
                value={tempFilters.propertyAddress}
                placeholder={
                  <span className="select-placeholder-text">
                    {labels.property} Address
                  </span>
                }
                isClearable
                components={{
                  Input: () => null,
                  DropdownIndicator: (props) =>
                    !tempFilters.propertyAddress ? (
                      <components.DropdownIndicator {...props} />
                    ) : null,
                  IndicatorSeparator: () => null,
                }}
              />
            </div>
          )}
        </FeatureGuard>
      </div>
      <div className={styles.filterBtnContainer}>
        <Button onClick={handleResetFilter} color="gray">
          Reset
        </Button>
        <Button onClick={handleApplyFilter}>Apply</Button>
      </div>
    </Modal>
  );
}
export default OrdersFilterModal;
